import {
  collection,
  type DocumentSnapshot,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';
import type { ApiResponse } from '../types/api';
import type { Section, Tender } from '../types/tender';
import { logErrorActivity } from '../utils/activityLogger';
import httpClient from './http/httpClient';
import { shareTenderWithWorkspaceAdmins } from './sharingService';
import storageService from './storageService';
import type { CreateTenderResponse, TenderType } from './tenderService';

export const getTenders = async (options?: { includeArchived?: boolean }): Promise<Tender[]> => {
  const queryParams = new URLSearchParams();
  if (options?.includeArchived) {
    queryParams.append('includeArchived', 'true');
  }
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  const response = await httpClient.get<{ tenders: Tender[] }>(`/getTenders${queryString}`);
  return response.tenders;
};

export const getPublicTenderStoreDetails = async (): Promise<Record<string, unknown>[]> => {
  const publicSnapshot = await getDocs(
    query(
      collection(firestore, 'publicTenderStoreDetails'),
      orderBy('llmProcessingTimestamp', 'desc')
    )
  );
  return publicSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getPrivateUserTenders = async (userId: string): Promise<Record<string, unknown>[]> => {
  const privateTendersColRef = collection(firestore, 'users', userId, 'privateUserTenders');
  const privateSnapshot = await getDocs(privateTendersColRef);
  return privateSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const createTender = async (
  files: File | File[],
  tenderType: TenderType = 'uploaded',
  metadata?: { title?: string; tenderIssuer?: string; isGrant?: boolean } // INTERIM WHITE-LABELING: isGrant
): Promise<CreateTenderResponse> => {
  const fileArray = Array.isArray(files) ? files : [files];

  const keyHeader = '';

  const body: Record<string, unknown> = {
    action: 'create_only',
    tenderType,
    title: metadata?.title,
    tenderIssuer: metadata?.tenderIssuer,
    isGrant: metadata?.isGrant,
  };

  // 1. Create the Tender document and get the upload path prefix
  const createResponse = await httpClient.post<
    CreateTenderResponse & { storagePathPrefix?: string }
  >('/addTenderToDashboard', body, {
    headers: {
      ...(keyHeader ? { 'X-Encryption-Key': keyHeader } : {}),
    },
    timeout: 0,
  });

  if (!createResponse.success || !createResponse.tenderId || !createResponse.storagePathPrefix) {
    throw new Error('Failed to initialize tender creation.');
  }

  // 2. Upload files directly to Firebase Storage using the provided prefix
  const originalFileNames: string[] = [];

  // Process in batches of 5 to avoid overwhelming the browser/network connections
  const CONCURRENCY_LIMIT = 5;
  for (let i = 0; i < fileArray.length; i += CONCURRENCY_LIMIT) {
    const batch = fileArray.slice(i, i + CONCURRENCY_LIMIT);
    const uploadPromises = batch.map(async (file) => {
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      originalFileNames.push(safeFileName);
      const filePath = `${createResponse.storagePathPrefix}${safeFileName}`;
      await storageService.uploadFile(filePath, file);
    });
    await Promise.all(uploadPromises);
  }

  // 3. Trigger conversion
  await httpClient.post(
    '/addTenderToDashboard',
    {
      action: 'trigger_conversion',
      tenderId: createResponse.tenderId,
      files: originalFileNames,
    },
    {
      headers: {
        ...(keyHeader ? { 'X-Encryption-Key': keyHeader } : {}),
      },
      timeout: 0,
    }
  );

  if (createResponse.success && createResponse.tenderId) {
    // Auto-share with admins
    shareTenderWithWorkspaceAdmins(createResponse.tenderId).catch((err) =>
      logErrorActivity.occurred({
        errorType: 'auto_share_error',
        errorMessage: 'Failed to auto-share with admins',
        userAction: 'create_tender',
        errorCode: (err as Error).name,
      })
    );
  }

  return createResponse;
};

export const updateTenderAdminOnly = async (tenderId: string, adminOnly: boolean) => {
  if (!tenderId) {
    throw new Error('Tender ID is required');
  }

  const docRef = doc(firestore, 'tenders', tenderId);
  await updateDoc(docRef, { adminOnly });
};

export const updateTenderNoFiles = async (tenderId: string, noFiles: boolean) => {
  if (!tenderId) {
    throw new Error('Tender ID is required');
  }

  const docRef = doc(firestore, 'tenders', tenderId);
  await updateDoc(docRef, { noFiles });
};

export const updateTenderQtipsCompleted = async (tenderId: string, qtipsCompleted: number[]) => {
  if (!tenderId) {
    throw new Error('Tender ID is required');
  }

  const docRef = doc(firestore, 'tenders', tenderId);

  await updateDoc(docRef, {
    qtipsCompleted,
  });
};

export const deleteTender = async (
  tenderId: string,
  deleteReason: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await httpClient.post<ApiResponse<{ message: string }>>('/deleteTender', {
    tenderId,
    deleteReason,
  });
  return response;
};

export const archiveTender = async (
  tenderId: string,
  archiveReason: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await httpClient.post<ApiResponse<{ message: string }>>('/archiveTender', {
    tenderId,
    archiveReason,
  });
  return response;
};

export const pursueTender = async (tenderId: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await httpClient.post<ApiResponse<{ message: string }>>('/pursueTender', {
    tenderId,
  });
  return response;
};

export const addTenderToDashboard = async (storeTenderId: string): Promise<ApiResponse<Tender>> => {
  if (!storeTenderId) {
    throw new Error('Store tender ID is required.');
  }

  const keyHeader = '';
  const body: Record<string, unknown> = { storeTenderId };

  const response = await httpClient.post<ApiResponse<Tender>>('/addTenderToDashboard', body, {
    headers: {
      ...(keyHeader ? { 'X-Encryption-Key': keyHeader } : {}),
    },
  });

  if (response.success && response.data?.id) {
    // Auto-share with admins
    shareTenderWithWorkspaceAdmins(response.data.id).catch((err) =>
      logErrorActivity.occurred({
        errorType: 'auto_share_error',
        errorMessage: 'Failed to auto-share with admins',
        userAction: 'add_store_tender',
        errorCode: (err as Error).name,
      })
    );
  }

  return response;
};

export const archiveStoreTender = async (
  storeTenderId: string,
  reason: string,
  tenderTitle: string
): Promise<ApiResponse<{ message: string }>> => {
  if (!storeTenderId) {
    throw new Error('Store tender ID is required.');
  }
  if (!reason) {
    throw new Error('An archive reason is required.');
  }
  const response = await httpClient.post<ApiResponse<{ message: string }>>('/archiveStoreTender', {
    storeTenderId,
    reason,
    tenderTitle,
  });
  return response;
};

export const listenToTender = (
  tenderId: string,
  onUpdate: (tender: Tender) => void,
  onError: (error: Error) => void
) => {
  const tenderRef = doc(firestore, 'tenders', tenderId);
  return onSnapshot(
    tenderRef,

    (docSnap: DocumentSnapshot) => {
      if (docSnap.exists()) {
        onUpdate({ id: docSnap.id, ...docSnap.data() } as Tender);
      } else {
        onError(new Error('Tender not found'));
      }
    },
    onError
  );
};

export const updateTender = async (
  tenderId: string,
  updateData: Partial<Tender> | Record<string, unknown>
): Promise<void> => {
  if (!tenderId) {
    throw new Error('Tender ID is required');
  }
  const docRef = doc(firestore, 'tenders', tenderId);
  // biome-ignore lint/suspicious/noExplicitAny: bypassed to avoid exactOptionalPropertyTypes issue
  await updateDoc(docRef, updateData as any);
};

export const subscribeToTenderSections = (
  tenderId: string,
  userId: string,
  hasViewAllSections: boolean,

  callback: (docs: Section[]) => void,
  onError: (error: Error) => void
) => {
  let sectionsQuery: import('firebase/firestore').Query = collection(
    firestore,
    `tenders/${tenderId}/sections`
  );

  if (!hasViewAllSections) {
    sectionsQuery = query(sectionsQuery, where('assignedTo.uid', '==', userId));
  }

  return onSnapshot(
    sectionsQuery,

    (snapshot: QuerySnapshot) => {
      const docsData = snapshot.docs.map(
        (doc: QueryDocumentSnapshot) =>
          ({
            ...doc.data(),
            id: doc.id,
          }) as Section
      );
      callback(docsData);
    },
    onError
  );
};
